import { FC, useState } from 'react';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { FaPen, FaListAlt, FaFile, FaFileSignature, FaInfo, FaFilePrescription } from 'react-icons/fa';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';
import { useActiveClientContext } from 'store/active-client';

import { formatDate } from 'utils/helpers/formatters';
import setServerErrors from 'utils/helpers/formErrors';
import { isIntigerOrFloat } from 'utils/helpers/leaves';
import { buildClientEntity } from 'utils/helpers/client';
import { useHistory } from 'react-router';

import Button from 'components/Button';
import FetchTable from 'components/table/FetchTable';
import TooltipWrapper from 'components/TooltipWrapper';
import { IАttachment } from 'components/Attachment';
import BadgeComponent from 'components/Badge';
import EmployeeRejectLeave from 'routes/client/leaves/components/EmployeeRejectLeave';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import Spinner from 'components/Spinner';
import handleEmployeeCode from 'utils/helpers/handleEmployeeCode';
import { HandleEmployeeSignOptions } from 'components/form/CodeForm';
import LeaveInterruptionInfoButton from '../../leaves/components/LeaveInterruptionInfoButton';
import { ILeave } from '../../leaves/types';
import { IMyLeave } from '../types';

const UpcomingLeaves: FC = () => {
  const { client } = useActiveClientContext();
  const { t } = useTranslation();
  const { showModal } = useModal();
  const queryClient = useQueryClient();
  const history = useHistory();
  const windowInnerWidth = window.innerWidth;
  const isMobile = windowInnerWidth < 768;
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [isLoadingReject, setIsLoadingReject] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const getTooltipContent = (leave: ILeave) => {
    return (
      <div>
        <b>{t('leaves.tooltip.id', { id: leave.id })}</b>
        {leave.reject_reason?.length > 0 && (
          <div>
            {t('leaves.rejectReason')}: {leave.reject_reason}
          </div>
        )}
      </div>
    );
  };

  const handleAttachmentClick = (id: number) => {
    return API.get(`leaves/attachment/${id}/download`)
      .then(res => {
        window.open(res.data, '_blank');
      })
      .catch(res => {
        const error = createErrorFromResponse(res);
        showModal({
          type: 'error',
          content: error.message,
        });
      });
  };

  const handleAttachmentsModal = (attachments: IАttachment[], commentText: string | undefined) => {
    const links = attachments.map(attachment => {
      return {
        id: attachment.id,
        fileName: `${attachment.file_name}.${attachment.extension}`,
      };
    });

    showModal({
      type: 'list',
      heading: t('leaves.attachmentsList'),
      onLinkClick: handleAttachmentClick,
      links,
      content: commentText ? (
        <span>
          <b style={{ fontSize: '20px' }}>Коментар от служител:</b> {commentText}
        </span>
      ) : undefined,
    });
  };

  const handleCommentsModal = (comment: string) => {
    showModal({
      type: 'info',
      heading: t('leaves.comment'),
      onLinkClick: handleAttachmentClick,
      content: comment,
    });
  };

  // Sign document as employee
  const handleEmployeeLeaveSign = (options: HandleEmployeeSignOptions) => {
    // Sign process used in "employee" mode.
    // AdES (Advanced Electronic Signature)
    //
    // The process takes place with one request to BE,
    // as the signing is done with self-signed certificates,
    // which are kept on the backend.
    const { docId, data, methodsProp } = options;
    return API.post(`documents/${docId}/sign-ades`, data)
      .then(res => {
        showModal({
          type: 'success',
          content: <>{t('leaves.createSuccessMsg')}.</>,
        });
        return queryClient.invalidateQueries('my-leaves');
      })
      .catch(response => {
        const error = createErrorFromResponse(response);
        if (methodsProp) {
          setServerErrors(error.errors, methodsProp.setError);
        } else {
          showModal({
            type: 'error',
            content: createErrorFromResponse(error).message,
          });
        }
      });
  };
  const columns: Array<Column<IMyLeave>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('leaves.type') || '',
      accessor: 'type_label',
      width: '20%',
      Cell: row => {
        return row.value || '';
      },
    },
    {
      Header: t('leaves.dateFrom') || '',
      accessor: 'date_from',
      width: '7.5rem',
      Cell: ({ cell }) => {
        const dateFrom = new Date(cell.row.original.date_from);
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-date-from-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{formatDate(dateFrom, 'dd.mm.YYY') || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{formatDate(dateFrom, 'dd.mm.YYY') || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.dateTo') || '',
      accessor: 'date_to',
      width: '7rem',
      Cell: ({ cell }) => {
        const dateTo = new Date(cell.row.original.date_to);
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-date-to-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{formatDate(dateTo, 'dd.mm.YYY') || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{formatDate(dateTo, 'dd.mm.YYY') || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.numberOfDays') || '',
      width: '5rem',
      accessor: 'leave_days',
      Cell: ({ cell }) => {
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;
        const leaveDays = cell.row.original.leave_days;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-leave-days-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{isIntigerOrFloat(leaveDays) || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{isIntigerOrFloat(leaveDays) || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.status') || '',
      accessor: 'status',
      Cell: ({ value, row }) => {
        const statuses: any = {
          initiated: t('leaves.initiated'),
          pending: t('leaves.pending'),
          signing: t('leaves.signing'),
          confirmed: t('leaves.confirmed'),
          approved: t('leaves.approved'),
          rejected: t('leaves.rejected'),
          cancelled: t('leaves.cancelled'),
          employeeRejectInitiated: t('leaves.confirmed'),
          interrupted: t('leaves.interrupted'),
        };
        return value === 'rejected' ? (
          <TooltipWrapper
            id={`tooltip-status-${row.original.id}`}
            overlay={getTooltipContent(row.original as unknown as ILeave)}
          >
            <span>
              <BadgeComponent
                variant="danger"
                label={statuses[value]}
                normalBadgeStyles
              />
            </span>
          </TooltipWrapper>
        ) : (
          statuses[value] || ''
        );
      },
    },
    {
      Header: () => <div className="text-right">{t('table.actions')}</div>,
      accessor: 'actions',
      width: '7.5rem',
      disableSortBy: true,
      Cell: cell => {
        const {
          request_document_id: requestDocument,
          order_document_id: orderDocument,
          reject_document_id: rejectDocument,
          reject_request_document_id: rejectionRequestDocument,
          status,
          attachments,
          buttons,
          comment: commentText,
          id: leaveId,
          interrupting_sick_leave: interruptingSickLeave,
          interrupted_leaves: interruptedLeaves,
        } = cell.row.original;
        const interruptionInfoShouldRender =
          interruptingSickLeave || (interruptedLeaves && interruptedLeaves.length > 0);
        return (
          <ButtonContainer>
            {status === 'initiated' && (
              <div className="d-inline-block">
                <TooltipWrapper
                  id="tooltip-sign"
                  overlay={t('documents.table.sign')}
                >
                  <Button
                    type="button"
                    btnType="info"
                    size="sm"
                    onClick={() =>
                      handleEmployeeCode({
                        docId: requestDocument,
                        showModal,
                        heading: t('documents.table.signWithCodeLabel'),
                        handleEmployeeSign: handleEmployeeLeaveSign,
                      })
                    }
                  >
                    <FaPen size={14} />
                  </Button>
                </TooltipWrapper>
              </div>
            )}{' '}
            {requestDocument && (
              <div className="d-inline-block">
                <TooltipWrapper
                  id="tooltip-approve"
                  overlay={t('leaves.requestDocPreview')}
                >
                  <Button
                    type="button"
                    btnType="info"
                    size="sm"
                    disabled={isLoadingDownload}
                    onClick={() =>
                      handlePreviewDoc({ showModal, id: requestDocument, setIsLoading: setIsLoadingDownload })
                    }
                  >
                    {!isLoadingDownload && <FaFile size={14} />}

                    {isLoadingDownload && <Spinner size={14} />}
                  </Button>
                </TooltipWrapper>
              </div>
            )}
            {orderDocument && (
              <div className="d-inline-block">
                <TooltipWrapper
                  id="tooltip-approve"
                  overlay={t('leaves.orderDocPreview')}
                >
                  <Button
                    type="button"
                    btnType="primary"
                    size="sm"
                    disabled={isLoadingOrder}
                    onClick={() => handlePreviewDoc({ showModal, id: orderDocument, setIsLoading: setIsLoadingOrder })}
                  >
                    {!isLoadingOrder && <FaFileSignature size={14} />}

                    {isLoadingOrder && <Spinner size={14} />}
                  </Button>
                </TooltipWrapper>
              </div>
            )}
            {rejectionRequestDocument && status === 'rejected' && (
              <div className="d-inline-block ml-2">
                <TooltipWrapper
                  id="tooltip-approve"
                  overlay={t('leaves.rejectionRequestDocPreview')}
                >
                  <Button
                    type="button"
                    btnType="danger"
                    size="sm"
                    disabled={isLoadingReject}
                    onClick={() =>
                      handlePreviewDoc({ showModal, id: rejectionRequestDocument, setIsLoading: setIsLoadingReject })
                    }
                  >
                    {!isLoadingReject && <FaFile size={14} />}

                    {isLoadingReject && <Spinner size={14} />}
                  </Button>
                </TooltipWrapper>
              </div>
            )}
            {rejectDocument && status === 'rejected' && (
              <div className="d-inline-block ml-2">
                <TooltipWrapper
                  id="tooltip-approve"
                  overlay={t('leaves.orderDocPreview')}
                >
                  <Button
                    type="button"
                    btnType="danger"
                    size="sm"
                    disabled={isLoadingPreview}
                    onClick={() =>
                      handlePreviewDoc({ showModal, id: rejectDocument, setIsLoading: setIsLoadingPreview })
                    }
                  >
                    {!isLoadingPreview && <FaFilePrescription size={14} />}
                  </Button>
                </TooltipWrapper>
              </div>
            )}
            {attachments.length > 0 && (
              <div className="d-inline-block">
                <TooltipWrapper
                  id="tooltip-attachments"
                  overlay={t('leaves.attachmentsList')}
                >
                  <Button
                    ariaLabel="Attachments List"
                    type="button"
                    btnType="info"
                    size="sm"
                    onClick={() => handleAttachmentsModal(attachments, commentText)}
                  >
                    <FaListAlt size={14} />
                  </Button>
                </TooltipWrapper>
              </div>
            )}
            {interruptionInfoShouldRender && (
              <LeaveInterruptionInfoButton
                key={`${leaveId}-interruption-info`}
                leaveId={leaveId}
                leaveDetails={cell.row.original as unknown as ILeave}
                marginLeft
              />
            )}
            {buttons.map((button: { type: string; text: string }, index: number) => {
              const { type, text } = button;
              if (type === 'comment') {
                return (
                  <div
                    className="d-inline-block"
                    key={`upc-leaves-${text}-${index}`}
                  >
                    <TooltipWrapper
                      id="tooltip-comment"
                      overlay={
                        <>
                          {text}: <br /> {commentText}
                        </>
                      }
                    >
                      <Button
                        type="button"
                        btnType="info"
                        size="sm"
                        onClick={() => handleCommentsModal(commentText)}
                      >
                        <FaInfo size={14} />
                      </Button>
                    </TooltipWrapper>
                  </div>
                );
              }
              if (type === 'employee_reject') {
                return (
                  <EmployeeRejectLeave
                    key={`upc-leaves-${text}-${index}`}
                    leaveId={leaveId}
                    buttonText={text}
                    invalidate={() => queryClient.invalidateQueries('my-leaves')}
                  />
                );
              }
              if (type === 'employee_reject_without_document') {
                return (
                  <EmployeeRejectLeave
                    key={`upc-leaves-${text}-${index}`}
                    leaveId={leaveId}
                    buttonText={text}
                    invalidate={() => queryClient.invalidateQueries('my-leaves')}
                    isWithoutDocument
                  />
                );
              }
              return null;
            })}
          </ButtonContainer>
        );
      },
    },
  ];

  const mobileColumns: Array<Column<IMyLeave>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('leaves.type') || '',
      accessor: 'type_label',
      width: '35%',
      Cell: row => {
        return row.value || '';
      },
    },
    {
      Header: t('leaves.dateFromTo') || '',
      accessor: 'date_from_to',
      width: '35%',
      Cell: ({ cell }) => {
        return `${formatDate(new Date(cell.row.original.date_from), 'dd.mm.YYY')} - ${formatDate(
          new Date(cell.row.original.date_to),
          'dd.mm.YYY'
        )}`;
      },
    },
    {
      Header: t('leaves.numberOfDays') || '',
      accessor: 'leave_days',
      width: '30%',
      Cell: ({ cell }) => {
        return isIntigerOrFloat(cell?.value) || '';
      },
    },
  ];

  return (
    <>
      <h3 className="h5 mb-4 mt-0 mt-xl-5">{t('leaves.upcomingTitle')}</h3>
      <FetchTable<IMyLeave>
        entityName={buildClientEntity('my-leaves', client)}
        entityEndpoint="leaves"
        columns={isMobile ? mobileColumns : columns}
        hideFilterBar
        isLeavesMobile
        pagination
      />
      <br />
      <Button
        type="button"
        btnType="info"
        size="sm"
        onClick={() => history.push('/my-leaves/previous')}
      >
        {t('leaves.previousLeavesInfo')}
      </Button>
    </>
  );
};
export default UpcomingLeaves;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;
